import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Checkbox,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
	CircularProgress,
	LinearProgress,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControlLabel,
	Grid,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EastIcon from '@mui/icons-material/East';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import {
	downloadBase64File,
	exportStage3AttestCsv,
	exportStage3AttestMergedPdf,
	exportStage3AttestSinglePdf,
	exportStage3AttestZip,
	exportStage3ShippingPostXlsx,
	convertStage3EnLabelsPdfToSheet,
	appendStage3SchoolCity,
	getStage3SchoolLocationPreview,
	getStage3AttestPreviewHtml,
	getStage3AttestSettings,
	getStage3AttestStudentDetail,
	getStage3AttestStudents,
	saveStage3AttestSettings,
	STAGE3_SYNC_TEXTS_BATCH_SIZE,
	syncStage3AttestTexts,
	updateStage3AttestRow,
	Stage3AttestCohort,
	Stage3AttestSettings,
	Stage3AttestStudentRow,
	Stage3SyncAreas,
} from '../../api/stage3-attestation-api';
import Stage3SignedScanDialog from './Stage3SignedScanDialog';
import EnShippingDialog from './EnShippingDialog';

const STAGE3_INFO_TOOLTIP =
	'Kohorty: Wszyscy — Finaliści IIIA — IIIB bez ustnego — Finaliści IIIB — Laureaci — bez numeru — R1 — Bez ZIP. Flaga „Bez ZIP” (exclude) pomija ucznia w zbiorczym ZIP pozostałych kohort; wyjątki: Wszyscy, R1, kohorta Bez ZIP. Zsynchronizuj teksty: partie po 50. Morfeusz2: olympics_pl_morphology_url, olympics_pl_morphology_token; lokalnie cloud-functions/pl-morphology.';

type Stage3SortKey =
	| 'certificate_number'
	| 'last_name'
	| 'first_name'
	| 'district_name'
	| 'final_title'
	| 'city_locative'
	| 'birth_date_words'
	| 'class_roman';

const COHORTS: { id: Stage3AttestCohort; label: string }[] = [
	{ id: 'all', label: 'Wszyscy' },
	{
		id: 'iiia_no_oral',
		label: 'Finaliści IIIA',
	},
	{
		id: 'iiib_no_oral',
		label: 'IIIB bez udziału w ustnym',
	},
	{
		id: 'iiib_finalist',
		label: 'Finaliści IIIB',
	},
	{ id: 'laureate', label: 'Laureaci IIIB' },
	{
		id: 'no_certificate_number',
		label: 'Z tytułem bez numeru',
	},
	{ id: 'manual_r1', label: 'R1 — poprawki (ręczna)' },
	{
		id: 'no_zip_excluded_from_cohorts',
		label: 'Bez ZIP',
	},
];

const Stage3AttestationsTab: React.FC = () => {
	const { token } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const [cohort, setCohort] = useState<Stage3AttestCohort>('all');
	const [settings, setSettings] = useState<Stage3AttestSettings | null>(null);
	const [rows, setRows] = useState<Stage3AttestStudentRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [previewIndex, setPreviewIndex] = useState(0);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewRecordJump, setPreviewRecordJump] = useState('');
	const previewBlobUrlRef = useRef<string | null>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [editRow, setEditRow] = useState<Stage3AttestStudentRow | null>(null);
	const [editSaving, setEditSaving] = useState(false);
	/** Morfologia dla propozycji po lewej — po otwarciu dialogu, w tle. */
	const [editSuggestionsLoading, setEditSuggestionsLoading] = useState(false);
	const editSuggestionsRequestRef = useRef(0);
	const [sortKey, setSortKey] = useState<Stage3SortKey>('last_name');
	const [sortAsc, setSortAsc] = useState(true);
	const [syncProgress, setSyncProgress] = useState<{ processed: number; total: number } | null>(null);
	const [cityDialogOpen, setCityDialogOpen] = useState(false);
	const [cityLocDraft, setCityLocDraft] = useState('');
	const [cityPrep, setCityPrep] = useState('w');
	const [cityPreviewLoading, setCityPreviewLoading] = useState(false);
	const [cityAppendBusy, setCityAppendBusy] = useState(false);
	const [excludeSavingId, setExcludeSavingId] = useState<number | null>(null);
	const [r1ManualSavingId, setR1ManualSavingId] = useState<number | null>(null);
	const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
	const [syncDialogOpen, setSyncDialogOpen] = useState(false);
	const [syncAreas, setSyncAreas] = useState<Stage3SyncAreas>({
		birth_dates: true,
		birth_place: true,
		school: true,
		class: true,
	});
	const [syncFillMissingOnly, setSyncFillMissingOnly] = useState(false);
	const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
	const [signedScanDialogOpen, setSignedScanDialogOpen] = useState(false);

	const filteredRows = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) {
			return rows;
		}
		return rows.filter((r) => {
			const hay = [
				r.first_name,
				r.last_name,
				r.school_genitive,
				r.school_name_original,
				r.final_title,
				r.title_declaration,
				r.lokata_text,
				r.district_name,
				r.city_locative,
				r.birthday_place,
				r.school_city,
			]
				.join(' ')
				.toLowerCase();
			return hay.includes(q);
		});
	}, [rows, searchQuery]);

	/** Kohortowy eksport ZIP/PDF — jedna treść linii dat (jak resolveExamDatesLine w PHP); „Wszyscy” / bez numeru: per uczeń. */
	const exportExamDatesLinePreview = useMemo(() => {
		if (!settings) {
			return { kind: 'none' as const };
		}
		const full = (settings.exam_dates_line ?? '').trim();
		const short = (settings.exam_dates_line_written_only ?? '').trim();
		if (
			cohort === 'all' ||
			cohort === 'no_certificate_number' ||
			cohort === 'manual_r1' ||
			cohort === 'no_zip_excluded_from_cohorts'
		) {
			return { kind: 'per_student' as const, full, short };
		}
		if (cohort === 'iiia_no_oral' || cohort === 'iiib_no_oral') {
			const effective = short || full;
			return {
				kind: 'single' as const,
				settingLabel: 'Linia z datami — tylko etap pisemny (w pliku, jeśli puste — używana pełna)',
				text: effective,
			};
		}
		return {
			kind: 'single' as const,
			settingLabel: 'Linia z datami — pisemny i ustny',
			text: full,
		};
	}, [settings, cohort]);

	const sortedFilteredRows = useMemo(() => {
		const dir = sortAsc ? 1 : -1;
		return [...filteredRows].sort((a, b) => {
			const va = a[sortKey];
			const vb = b[sortKey];
			const sa = va === null || va === undefined ? '' : String(va);
			const sb = vb === null || vb === undefined ? '' : String(vb);
			if (sortKey === 'certificate_number') {
				const na = parseInt(sa, 10);
				const nb = parseInt(sb, 10);
				if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) {
					return (na - nb) * dir;
				}
			}
			return sa.localeCompare(sb, 'pl', { sensitivity: 'base' }) * dir;
		});
	}, [filteredRows, sortKey, sortAsc]);

	const openCitySuffixDialog = useCallback(async () => {
		if (!token || !editRow) {
			return;
		}
		setCityDialogOpen(true);
		setCityPreviewLoading(true);
		try {
			const res = await getStage3SchoolLocationPreview(token, editRow.student_id);
			const d = res.data;
			if (d.ok && d.city_locative) {
				setCityLocDraft(d.city_locative);
				setCityPrep(d.preposition ?? 'w');
			} else {
				setMsg({ msg: d.error ?? 'Brak danych o mieście szkoły', severity: 'warning' });
				setCityLocDraft('');
				setCityPrep('w');
			}
		} catch {
			setMsg({ msg: 'Nie udało się wczytać podglądu miasta', severity: 'error' });
			setCityLocDraft('');
		} finally {
			setCityPreviewLoading(false);
		}
	}, [token, editRow, setMsg]);

	const applyCitySuffix = useCallback(async () => {
		if (!token || !editRow) {
			return;
		}
		setCityAppendBusy(true);
		try {
			const res = await appendStage3SchoolCity(
				token,
				editRow.student_id,
				cityLocDraft.trim() !== '' ? cityLocDraft : undefined,
			);
			const updated = res.data.student;
			if (updated) {
				setEditRow(updated);
				setRows((prev) => prev.map((r) => (r.student_id === updated.student_id ? updated : r)));
				setMsg({ msg: 'Zaktualizowano nazwę szkoły w bazie i dopełniacz zaświadczenia', severity: 'success' });
				setCityDialogOpen(false);
			}
		} catch (e: any) {
			const err = e?.response?.data?.error ?? e?.response?.data?.message;
			setMsg({ msg: typeof err === 'string' ? err : 'Błąd zapisu', severity: 'error' });
		} finally {
			setCityAppendBusy(false);
		}
	}, [token, editRow, cityLocDraft, setMsg]);

	const handleSort = (key: Stage3SortKey) => {
		if (sortKey === key) {
			setSortAsc((v) => !v);
		} else {
			setSortKey(key);
			setSortAsc(true);
		}
	};

	const previewCount = sortedFilteredRows.length;
	const displayPreviewIndex = previewCount > 0 ? Math.min(Math.max(0, previewIndex), previewCount - 1) : 0;

	useEffect(() => {
		if (previewCount > 0) {
			setPreviewRecordJump(String(displayPreviewIndex + 1));
		}
	}, [displayPreviewIndex, previewCount]);

	const commitPreviewRecordInput = useCallback(() => {
		if (previewCount === 0 || previewLoading) {
			return;
		}
		const n = parseInt(previewRecordJump.replace(/\s/g, ''), 10);
		if (!Number.isFinite(n)) {
			setPreviewRecordJump(String(displayPreviewIndex + 1));
			return;
		}
		const idx = Math.max(0, Math.min(previewCount - 1, n - 1));
		if (idx !== displayPreviewIndex) {
			setPreviewIndex(idx);
			setPage(Math.floor(idx / rowsPerPage));
		} else {
			setPreviewRecordJump(String(displayPreviewIndex + 1));
		}
	}, [previewCount, previewLoading, previewRecordJump, displayPreviewIndex, rowsPerPage]);

	const goToStudentPreview = useCallback(
		(r: Stage3AttestStudentRow) => {
			const idx = sortedFilteredRows.findIndex((x) => x.student_id === r.student_id);
			if (idx >= 0) {
				setPreviewIndex(idx);
				setPage(Math.floor(idx / rowsPerPage));
			}
		},
		[sortedFilteredRows, rowsPerPage],
	);

	const closeStage3EditDialog = useCallback(() => {
		setEditOpen(false);
		setEditSuggestionsLoading(false);
		editSuggestionsRequestRef.current += 1;
	}, []);

	const openStage3Edit = useCallback(
		(r: Stage3AttestStudentRow) => {
			const req = ++editSuggestionsRequestRef.current;
			setEditRow(r);
			setEditOpen(true);
			setEditSuggestionsLoading(true);
			if (!token) {
				setEditSuggestionsLoading(false);
				return;
			}
			void (async () => {
				try {
					const res = await getStage3AttestStudentDetail(token, r.student_id);
					if (req !== editSuggestionsRequestRef.current) {
						return;
					}
					const d = res.data.student;
					if (!d) {
						return;
					}
					setEditRow((prev) => {
						if (!prev || prev.student_id !== d.student_id) {
							return prev;
						}
						return {
							...prev,
							birthday_db: d.birthday_db,
							birthday_stored_raw: d.birthday_stored_raw,
							birth_date_words_suggested: d.birth_date_words_suggested,
							city_locative_suggested: d.city_locative_suggested,
							school_genitive_suggested: d.school_genitive_suggested,
							class_roman_suggested: d.class_roman_suggested,
							r1_manual: d.r1_manual,
						};
					});
				} catch {
					if (req === editSuggestionsRequestRef.current) {
						setMsg({
							msg: 'Nie udało się pobrać propozycji (morfologia). Edycja działa — możesz spróbować ponownie.',
							severity: 'warning',
						});
					}
				} finally {
					if (req === editSuggestionsRequestRef.current) {
						setEditSuggestionsLoading(false);
					}
				}
			})();
		},
		[token, setMsg],
	);

	const loadSettings = useCallback(async () => {
		if (!token) {
			return;
		}
		const res = await getStage3AttestSettings(token);
		setSettings(res.data);
	}, [token]);

	const loadStudents = useCallback(async () => {
		if (!token) {
			return;
		}
		setLoading(true);
		try {
			const res = await getStage3AttestStudents(token, cohort);
			setRows(res.data.students ?? []);
		} catch (e: unknown) {
			setMsg({ msg: 'Nie udało się wczytać listy uczniów', severity: 'error' });
		} finally {
			setLoading(false);
		}
	}, [token, cohort, setMsg]);

	useEffect(() => {
		loadSettings().catch(() => setMsg({ msg: 'Błąd wczytywania ustawień', severity: 'error' }));
	}, [loadSettings, setMsg]);

	useEffect(() => {
		loadStudents();
	}, [loadStudents]);

	useEffect(() => {
		setPreviewIndex(0);
		setPage(0);
	}, [cohort]);

	useEffect(() => {
		setPreviewIndex(0);
		setPage(0);
	}, [searchQuery]);

	useEffect(() => {
		setPreviewIndex(0);
	}, [sortKey, sortAsc]);

	useEffect(() => {
		if (previewCount > 0 && previewIndex > previewCount - 1) {
			setPreviewIndex(previewCount - 1);
		}
	}, [previewCount, previewIndex]);

	useEffect(() => {
		const maxPage = Math.max(0, Math.ceil(sortedFilteredRows.length / rowsPerPage) - 1);
		if (page > maxPage) {
			setPage(maxPage);
		}
	}, [sortedFilteredRows.length, rowsPerPage, page]);

	useEffect(() => {
		return () => {
			if (previewBlobUrlRef.current) {
				URL.revokeObjectURL(previewBlobUrlRef.current);
				previewBlobUrlRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (!token || previewCount === 0) {
			if (previewBlobUrlRef.current) {
				URL.revokeObjectURL(previewBlobUrlRef.current);
				previewBlobUrlRef.current = null;
			}
			setPreviewUrl(null);
			setPreviewLoading(false);
			return;
		}
		const studentId = sortedFilteredRows[displayPreviewIndex].student_id;
		let cancelled = false;
		setPreviewLoading(true);
		getStage3AttestPreviewHtml(token, studentId, cohort)
			.then((res) => {
				if (cancelled) {
					return;
				}
				if (previewBlobUrlRef.current) {
					URL.revokeObjectURL(previewBlobUrlRef.current);
					previewBlobUrlRef.current = null;
				}
				const u = URL.createObjectURL(new Blob([res.data.html], { type: 'text/html;charset=utf-8' }));
				previewBlobUrlRef.current = u;
				setPreviewUrl(u);
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				if (previewBlobUrlRef.current) {
					URL.revokeObjectURL(previewBlobUrlRef.current);
					previewBlobUrlRef.current = null;
				}
				setPreviewUrl(null);
				setMsg({ msg: 'Nie udało się wczytać podglądu HTML', severity: 'error' });
			})
			.finally(() => {
				if (!cancelled) {
					setPreviewLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [token, cohort, sortedFilteredRows, displayPreviewIndex, previewCount, setMsg]);

	const onCohortChange = (e: SelectChangeEvent<Stage3AttestCohort>) => {
		setCohort(e.target.value as Stage3AttestCohort);
	};

	const onSaveSettings = async () => {
		if (!token || !settings) {
			return;
		}
		setBusy(true);
		try {
			const res = await saveStage3AttestSettings(token, settings);
			setSettings(res.data);
			setMsg({ msg: 'Zapisano ustawienia', severity: 'success' });
		} catch {
			setMsg({ msg: 'Błąd zapisu ustawień', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onSync = async (areas: Stage3SyncAreas) => {
		if (!token) {
			return;
		}
		const bd = areas.birth_dates !== false;
		const bp = areas.birth_place !== false;
		const sc = areas.school !== false;
		const cl = areas.class !== false;
		if (!bd && !bp && !sc && !cl) {
			setMsg({ msg: 'Zaznacz co najmniej jeden obszar synchronizacji.', severity: 'warning' });
			return;
		}
		const payload: Stage3SyncAreas = { birth_dates: bd, birth_place: bp, school: sc, class: cl };
		setSyncDialogOpen(false);
		setBusy(true);
		setSyncProgress(null);
		let offset = 0;
		let totalUpdated = 0;
		const allErrors: { student_id: number; message: string }[] = [];
		try {
			while (true) {
				const res = await syncStage3AttestTexts(
					token,
					cohort,
					offset,
					STAGE3_SYNC_TEXTS_BATCH_SIZE,
					payload,
					syncFillMissingOnly,
				);
				const data = res.data;
				if (data.error && data.message) {
					setMsg({ msg: `Synchronizacja: ${data.message}`, severity: 'error' });
					console.error('sync-texts', data);
					return;
				}
				totalUpdated += data.updated ?? 0;
				if (data.errors?.length) {
					allErrors.push(...data.errors);
				}
				const total = typeof data.total === 'number' ? data.total : 0;
				const processed =
					typeof data.processed_total === 'number'
						? data.processed_total
						: offset + (data.batch_processed ?? data.updated ?? 0);
				setSyncProgress({ processed, total });
				const finished =
					data.done === true ||
					(total > 0 && processed >= total) ||
					(data.done === undefined && data.total === undefined);
				if (finished) {
					break;
				}
				offset = typeof data.processed_total === 'number' ? data.processed_total : processed;
			}
			const errN = allErrors.length;
			if (errN > 0) {
				console.warn('Częściowe błędy sync (wszystkie partie, uczeń → komunikat):', allErrors);
				setMsg({
					msg: `Zsynchronizowano ${totalUpdated} uczniów (wszystkie partie); ${errN} wierszy z błędem (konsola).`,
					severity: 'warning',
				});
			} else {
				setMsg({ msg: `Zsynchronizowano teksty (${totalUpdated} uczniów, kohorta)`, severity: 'success' });
			}
			await loadStudents();
		} catch (e: unknown) {
			const ax = e as { response?: { data?: { message?: string; error?: string } } };
			const m = ax.response?.data?.message || ax.response?.data?.error;
			setMsg({ msg: m ? `Błąd synchronizacji: ${m}` : 'Błąd synchronizacji (500)', severity: 'error' });
			console.error('sync-texts', e);
		} finally {
			setBusy(false);
			setSyncProgress(null);
		}
	};

	const onCsv = async () => {
		if (!token) {
			return;
		}
		setBusy(true);
		try {
			const res = await exportStage3AttestCsv(token, cohort);
			downloadBase64File(res.data.file_base64, res.data.filename);
			setMsg({ msg: 'Pobrano CSV', severity: 'info' });
		} catch {
			setMsg({ msg: 'Błąd eksportu CSV', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onSinglePdf = async (mode: 'preview' | 'numbered') => {
		if (!token || previewCount === 0) {
			return;
		}
		const sid = sortedFilteredRows[displayPreviewIndex].student_id;
		setBusy(true);
		try {
			const res = await exportStage3AttestSinglePdf(token, sid, mode, cohort);
			downloadBase64File(res.data.file_base64, res.data.filename, 'application/pdf');
			setMsg({ msg: 'Pobrano PDF', severity: 'info' });
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			setMsg({ msg: err.response?.data?.error || 'Błąd generowania PDF', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onZip = async (mode: 'preview' | 'numbered') => {
		if (!token) {
			return;
		}
		setBusy(true);
		try {
			const res = await exportStage3AttestZip(token, cohort, mode);
			downloadBase64File(res.data.file_base64, res.data.filename);
			setMsg({ msg: 'Pobrano archiwum ZIP', severity: 'info' });
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			const m = err.response?.data?.error;
			setMsg({ msg: m || 'Błąd generowania ZIP', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onMergedPdf = async (mode: 'preview' | 'numbered') => {
		if (!token) {
			return;
		}
		setBusy(true);
		try {
			const res = await exportStage3AttestMergedPdf(token, cohort, mode);
			downloadBase64File(res.data.file_base64, res.data.filename, 'application/pdf');
			setMsg({ msg: 'Pobrano PDF zbiorczy', severity: 'info' });
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			const m = err.response?.data?.error;
			setMsg({ msg: m || 'Błąd generowania PDF zbiorczego', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const toggleExcludeFromBulkExport = async (r: Stage3AttestStudentRow, checked: boolean) => {
		if (!token) {
			return;
		}
		setExcludeSavingId(r.student_id);
		try {
			const res = await updateStage3AttestRow(token, r.student_id, { exclude_from_bulk_export: checked });
			const updated = res.data.student;
			const sid = r.student_id;
			if (updated) {
				setRows((prev) => prev.map((row) => (row.student_id === sid ? updated : row)));
				setEditRow((cur) => (cur?.student_id === sid ? updated : cur));
			} else {
				setRows((prev) =>
					prev.map((row) => (row.student_id === sid ? { ...row, exclude_from_bulk_export: checked } : row)),
				);
				setEditRow((cur) => (cur?.student_id === sid ? { ...cur, exclude_from_bulk_export: checked } : cur));
			}
		} catch {
			setMsg({ msg: 'Błąd zapisu wykluczenia', severity: 'error' });
		} finally {
			setExcludeSavingId(null);
		}
	};

	const toggleR1Manual = async (r: Stage3AttestStudentRow, checked: boolean) => {
		if (!token) {
			return;
		}
		setR1ManualSavingId(r.student_id);
		try {
			const res = await updateStage3AttestRow(token, r.student_id, { r1_manual: checked });
			const updated = res.data.student;
			const sid = r.student_id;
			if (updated) {
				setRows((prev) => prev.map((row) => (row.student_id === sid ? updated : row)));
				setEditRow((cur) => (cur?.student_id === sid ? updated : cur));
			} else {
				setRows((prev) => prev.map((row) => (row.student_id === sid ? { ...row, r1_manual: checked } : row)));
				setEditRow((cur) => (cur?.student_id === sid ? { ...cur, r1_manual: checked } : cur));
			}
		} catch {
			setMsg({ msg: 'Błąd zapisu flagi R1', severity: 'error' });
		} finally {
			setR1ManualSavingId(null);
		}
	};

	return (
		<Box sx={{ maxWidth: 1400 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
				<Typography variant='h6'>Zaświadczenia — etap III</Typography>
				<Tooltip title={STAGE3_INFO_TOOLTIP}>
					<IconButton
						size='small'
						aria-label='Informacje o kohortach i synchronizacji'
					>
						<InfoOutlinedIcon fontSize='small' />
					</IconButton>
				</Tooltip>
			</Box>

			<FormControl
				sx={{ minWidth: 420, mb: 2 }}
				size='small'
			>
				<InputLabel id='cohort-label'>Kohorta</InputLabel>
				<Select<Stage3AttestCohort>
					labelId='cohort-label'
					label='Kohorta'
					value={cohort}
					onChange={onCohortChange}
				>
					{COHORTS.map((c) => (
						<MenuItem
							key={c.id}
							value={c.id}
						>
							{c.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{syncProgress && (
				<Box sx={{ width: '100%', maxWidth: 560, mb: 1.5 }}>
					<Typography
						variant='body2'
						color='text.secondary'
						sx={{ mb: 0.5 }}
					>
						Synchronizacja tekstów: {syncProgress.processed}
						{syncProgress.total > 0 ? ` / ${syncProgress.total}` : '…'}
					</Typography>
					<LinearProgress
						variant={syncProgress.total > 0 ? 'determinate' : 'indeterminate'}
						value={syncProgress.total > 0 ? Math.min(100, (100 * syncProgress.processed) / syncProgress.total) : 0}
					/>
				</Box>
			)}
			<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, mb: 2 }}>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
					<Button
						variant='contained'
						disabled={busy}
						onClick={() => setSyncDialogOpen(true)}
					>
						Zsynchronizuj teksty (kohorta)
					</Button>
					<Button
						variant='outlined'
						disabled={busy}
						onClick={() => setDownloadDialogOpen(true)}
					>
						Pobierz
					</Button>
					<Button
						variant='outlined'
						disabled={busy || !token}
						onClick={() => setShippingDialogOpen(true)}
					>
						Wysyłka
					</Button>
					<Button
						variant='outlined'
						disabled={busy || !token}
						onClick={() => setSignedScanDialogOpen(true)}
					>
						Wyślij skany
					</Button>
					{busy && <CircularProgress size={28} />}
				</Box>
			</Box>

			{settings && (
				<Accordion
					defaultExpanded={false}
					sx={{ maxWidth: 900, mb: 2 }}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography variant='subtitle1'>Ustawienia treści (mPDF / szablon)</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
						<Typography
							variant='subtitle2'
							component='div'
							sx={{ fontWeight: 600 }}
						>
							Import XLS — Elektroniczny Nadawca (płatnik / nadawca)
						</Typography>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Opcjonalnie: te same dane w każdym wierszu w kolumnach Platnik* szablonu EN (np. IBL lub Komitet Główny).
							Zgodnie z dokumentacją Poczty — pole „Płatnik” w EN zależy od wybranej usługi.
						</Typography>
						<TextField
							label='Platnik (kod wg EN, opcjonalnie)'
							value={settings.en_xlsx_platnik_rodzaj ?? ''}
							onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_rodzaj: e.target.value })}
							fullWidth
							size='small'
						/>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
							<TextField
								label='Platnik — nazwa'
								value={settings.en_xlsx_platnik_nazwa ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_nazwa: e.target.value })}
								sx={{ flex: '1 1 200px', minWidth: 180 }}
								size='small'
							/>
							<TextField
								label='Platnik — nazwa cd.'
								value={settings.en_xlsx_platnik_nazwa_cd ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_nazwa_cd: e.target.value })}
								sx={{ flex: '1 1 200px', minWidth: 180 }}
								size='small'
							/>
						</Box>
						<TextField
							label='Platnik — ulica'
							value={settings.en_xlsx_platnik_ulica ?? ''}
							onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_ulica: e.target.value })}
							fullWidth
							size='small'
						/>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
							<TextField
								label='Platnik — nr domu'
								value={settings.en_xlsx_platnik_dom ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_dom: e.target.value })}
								sx={{ flex: '1 1 120px', minWidth: 100 }}
								size='small'
							/>
							<TextField
								label='Platnik — nr lokalu'
								value={settings.en_xlsx_platnik_lokal ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_lokal: e.target.value })}
								sx={{ flex: '1 1 120px', minWidth: 100 }}
								size='small'
							/>
							<TextField
								label='Platnik — kod pocztowy'
								value={settings.en_xlsx_platnik_kod ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_kod: e.target.value })}
								sx={{ flex: '1 1 140px', minWidth: 120 }}
								size='small'
							/>
							<TextField
								label='Platnik — miejscowość'
								value={settings.en_xlsx_platnik_miejscowosc ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_miejscowosc: e.target.value })}
								sx={{ flex: '1 1 180px', minWidth: 160 }}
								size='small'
							/>
						</Box>
						<TextField
							label='Platnik — NIP (opcjonalnie)'
							value={settings.en_xlsx_platnik_nip ?? ''}
							onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_nip: e.target.value })}
							sx={{ maxWidth: 280 }}
							size='small'
						/>
						<TextField
							label='Blok kontaktowy IBL (prawy górny róg)'
							multiline
							minRows={4}
							value={settings.ibl_contact_block ?? ''}
							onChange={(e) => setSettings({ ...settings, ibl_contact_block: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Linia miejsca i daty wystawienia (jedna linia, np. Warszawa, …)'
							value={settings.issue_line}
							onChange={(e) => setSettings({ ...settings, issue_line: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Linia z datami zawodów — pisemny i ustny (środek dokumentu)'
							multiline
							minRows={2}
							value={settings.exam_dates_line}
							onChange={(e) => setSettings({ ...settings, exam_dates_line: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Linia z datami — tylko etap pisemny (krótsza, np. IIIA, IIIB bez ustnego)'
							multiline
							minRows={2}
							value={settings.exam_dates_line_written_only ?? ''}
							onChange={(e) => setSettings({ ...settings, exam_dates_line_written_only: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Funkcja podpisującego (nad polem na podpis)'
							multiline
							minRows={3}
							value={settings.signatory_title ?? ''}
							onChange={(e) => setSettings({ ...settings, signatory_title: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Imię i nazwisko podpisującego (kursywa)'
							value={settings.signatory_name ?? ''}
							onChange={(e) => setSettings({ ...settings, signatory_name: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Rok w „Nr …/ROK” (puste = bieżący rok kalendarzowy)'
							value={settings.certificate_year ?? ''}
							onChange={(e) => setSettings({ ...settings, certificate_year: e.target.value })}
							sx={{ maxWidth: 280 }}
						/>
						<TextField
							label='Szerokość numeru w nazwie pliku (3–8)'
							type='number'
							inputProps={{ min: 3, max: 8 }}
							value={settings.number_width}
							onChange={(e) =>
								setSettings({ ...settings, number_width: Math.max(3, Math.min(8, Number(e.target.value) || 5)) })
							}
							sx={{ maxWidth: 200 }}
						/>
						<TextField
							label='Światło przed dolną stopką (logotypy MEN/IBL), mm — 12–100'
							type='number'
							inputProps={{ min: 12, max: 100, step: 1 }}
							value={Math.round(settings.stage3_sp_footer_logos_space_before_mm ?? 50)}
							onChange={(e) => {
								const v = Math.max(12, Math.min(100, Number(e.target.value) || 50));
								setSettings({ ...settings, stage3_sp_footer_logos_space_before_mm: v });
							}}
							sx={{ maxWidth: 280 }}
						/>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Konwersja PDF etykiet EN → arkusz A4 (105×57 mm): przesunięcie siatki w dół (+) / w górę (−), mm (−20…40);
							dodatkowy odstęp między rzędami etykiet (0…10 mm).
						</Typography>
						<TextField
							label='Przesunięcie siatki etykiet (mm)'
							type='number'
							inputProps={{ min: -20, max: 40, step: 0.5 }}
							value={settings.en_label_sheet_offset_top_mm ?? 0}
							onChange={(e) => {
								const v = Math.max(-20, Math.min(40, Number(e.target.value) || 0));
								setSettings({ ...settings, en_label_sheet_offset_top_mm: v });
							}}
							sx={{ maxWidth: 280 }}
							helperText='Wartości dodatnie przesuwają całość w dół.'
						/>
						<TextField
							label='Dodatkowy odstęp między rzędami (mm)'
							type='number'
							inputProps={{ min: 0, max: 10, step: 0.5 }}
							value={settings.en_label_sheet_row_gap_extra_mm ?? 0}
							onChange={(e) => {
								const v = Math.max(0, Math.min(10, Number(e.target.value) || 0));
								setSettings({ ...settings, en_label_sheet_row_gap_extra_mm: v });
							}}
							sx={{ maxWidth: 280 }}
						/>
						<FormControl
							size='small'
							sx={{ maxWidth: 360 }}
						>
							<InputLabel id='signed-scan-preview-w-label'>Szerokość podglądu PDF skanów (%)</InputLabel>
							<Select<number>
								labelId='signed-scan-preview-w-label'
								label='Szerokość podglądu PDF skanów (%)'
								value={Math.min(75, Math.max(50, settings.signed_scan_preview_width_pct ?? 60))}
								onChange={(e: SelectChangeEvent<number>) => {
									const v = Number(e.target.value);
									setSettings({ ...settings, signed_scan_preview_width_pct: v });
								}}
							>
								{[50, 55, 60, 65, 70, 75].map((v) => (
									<MenuItem
										key={v}
										value={v}
									>
										{v}% — podgląd obok danych ucznia
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button
							variant='contained'
							disabled={busy}
							onClick={onSaveSettings}
							sx={{ alignSelf: 'flex-start' }}
						>
							Zapisz ustawienia
						</Button>
					</AccordionDetails>
				</Accordion>
			)}

			<Divider sx={{ my: 2 }} />
			<Typography
				variant='subtitle1'
				sx={{ mb: 1 }}
			>
				Podgląd dokumentu (HTML)
			</Typography>
			<TextField
				size='small'
				label='Szukaj (imię, nazwisko, szkoła, tytuł, FIN./LAUR., okręg, miasto)'
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				sx={{ mb: 1, maxWidth: 560 }}
				fullWidth
			/>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
				<Button
					size='small'
					variant='outlined'
					aria-label='Pierwszy rekord'
					disabled={previewCount === 0 || displayPreviewIndex <= 0 || previewLoading}
					onClick={() => {
						setPreviewIndex(0);
						setPage(0);
					}}
					sx={{ minWidth: 40, px: 0.75 }}
				>
					{'<<'}
				</Button>
				<Button
					size='small'
					variant='outlined'
					aria-label='Poprzedni rekord'
					disabled={previewCount === 0 || displayPreviewIndex <= 0 || previewLoading}
					onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
					sx={{ minWidth: 36, px: 0.75 }}
				>
					{'<'}
				</Button>
				<Tooltip title='Numer rekordu na aktualnej liście (wyszukiwanie i sort). Enter lub opuszczenie pola — przejście.'>
					<TextField
						size='small'
						label='Nr'
						value={previewRecordJump}
						onChange={(e) => setPreviewRecordJump(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								commitPreviewRecordInput();
							}
						}}
						onBlur={() => {
							commitPreviewRecordInput();
						}}
						sx={{ width: 56 }}
						disabled={previewCount === 0 || previewLoading}
						inputProps={{ inputMode: 'numeric', 'aria-label': 'Numer rekordu podglądu' }}
					/>
				</Tooltip>
				{previewCount > 0 && sortedFilteredRows[displayPreviewIndex] && (
					<Typography
						variant='body2'
						component='span'
						sx={{
							minWidth: 0,
							maxWidth: { xs: '100%', sm: 420 },
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
					>
						{' '}
						z {previewCount} - {sortedFilteredRows[displayPreviewIndex].last_name}{' '}
						{sortedFilteredRows[displayPreviewIndex].first_name}
					</Typography>
				)}
				{previewCount === 0 && (
					<Typography
						variant='body2'
						color='text.secondary'
						component='span'
					>
						brak rekordów
					</Typography>
				)}
				<Button
					size='small'
					variant='outlined'
					aria-label='Następny rekord'
					disabled={previewCount === 0 || displayPreviewIndex >= previewCount - 1 || previewLoading}
					onClick={() => setPreviewIndex((i) => Math.min(previewCount - 1, i + 1))}
					sx={{ minWidth: 36, px: 0.75 }}
				>
					{'>'}
				</Button>
				<Button
					size='small'
					variant='outlined'
					aria-label='Ostatni rekord'
					disabled={previewCount === 0 || displayPreviewIndex >= previewCount - 1 || previewLoading}
					onClick={() => {
						const last = previewCount - 1;
						setPreviewIndex(last);
						setPage(Math.floor(last / rowsPerPage));
					}}
					sx={{ minWidth: 40, px: 0.75 }}
				>
					{'>>'}
				</Button>
				<Button
					size='small'
					variant='outlined'
					disabled={previewCount === 0 || previewLoading || !sortedFilteredRows[displayPreviewIndex]}
					onClick={() => {
						const r = sortedFilteredRows[displayPreviewIndex];
						if (r) {
							openStage3Edit(r);
						}
					}}
				>
					Edytuj ten rekord
				</Button>
				<Button
					size='small'
					variant='contained'
					disabled={busy || previewCount === 0 || previewLoading}
					onClick={() => onSinglePdf('preview')}
				>
					PDF PODGLĄD
				</Button>
				<Button
					size='small'
					variant='outlined'
					disabled={busy || previewCount === 0 || previewLoading}
					onClick={() => onSinglePdf('numbered')}
				>
					PDF — z numerem
				</Button>
				{previewLoading && <CircularProgress size={22} />}
			</Box>
			<Box
				sx={{
					border: 1,
					borderColor: 'divider',
					borderRadius: 1,
					height: { xs: 780, md: 1170 },
					mb: 3,
					bgcolor: 'grey.100',
					overflow: 'hidden',
					width: '100%',
					maxWidth: { xs: '100%', md: '65%' },
					mx: 'auto',
				}}
			>
				{previewUrl ? (
					<iframe
						title='Podgląd zaświadczenia HTML'
						src={previewUrl}
						style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
					/>
				) : (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							p: 2,
						}}
					>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							{loading
								? 'Wczytywanie listy…'
								: previewCount === 0
									? 'Brak uczniów w tej kohortzie — wybierz inną lub zsynchronizuj teksty.'
									: previewLoading
										? 'Generowanie podglądu…'
										: 'Brak podglądu.'}
						</Typography>
					</Box>
				)}
			</Box>

			<Typography
				variant='subtitle1'
				sx={{ mb: 1 }}
			>
				Podgląd danych
				{searchQuery.trim() ? ` (${filteredRows.length} z ${rows.length})` : ` (${rows.length})`}
			</Typography>
			<TableContainer
				component={Paper}
				sx={{ maxHeight: 682, overflow: 'auto' }}
			>
				<Table
					size='small'
					stickyHeader
				>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 44, px: 0.5 }}> </TableCell>
							<TableCell sx={{ maxWidth: 56, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'certificate_number'}
									direction={sortKey === 'certificate_number' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('certificate_number')}
								>
									Nr
								</TableSortLabel>
							</TableCell>
							<TableCell
								align='center'
								sx={{ maxWidth: 52, px: 0.25 }}
							>
								<Tooltip title='Zaznacz, aby pominąć tego ucznia w ZIP i w jednym PDF zbiorczym (np. zaświadczenie już odebrane).'>
									<span>Bez ZIP</span>
								</Tooltip>
							</TableCell>
							<TableCell
								align='center'
								sx={{ maxWidth: 44, px: 0.25 }}
							>
								<Tooltip title='Lista R1 — poprawki (kohorta „R1 — poprawki”, mailing, skany, ponowny proces).'>
									<span>R1</span>
								</Tooltip>
							</TableCell>
							<TableCell sx={{ maxWidth: 120, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'last_name' || sortKey === 'first_name'}
									direction={(sortKey === 'last_name' || sortKey === 'first_name') && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('last_name')}
								>
									Nazwisko / imię
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 72, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'district_name'}
									direction={sortKey === 'district_name' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('district_name')}
								>
									Okręg
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 200, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'final_title'}
									direction={sortKey === 'final_title' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('final_title')}
								>
									Tytuł / FIN./LAUR. / lokata
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 120, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'city_locative'}
									direction={sortKey === 'city_locative' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('city_locative')}
								>
									M. (baza) / w …
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 120, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'birth_date_words' || sortKey === 'class_roman'}
									direction={(sortKey === 'birth_date_words' || sortKey === 'class_roman') && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('birth_date_words')}
								>
									Data ur. / klasa
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 120, px: 0.5 }}>Sz. (baza)</TableCell>
							<TableCell sx={{ maxWidth: 140, px: 0.5 }}>Szkoła dop.</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={11}>Wczytywanie…</TableCell>
							</TableRow>
						) : filteredRows.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={11}
									sx={{ py: 3 }}
								>
									{rows.length === 0
										? 'Brak uczniów w kohortcie.'
										: 'Brak wyników dla wpisanego tekstu — wyczyść pole wyszukiwania.'}
								</TableCell>
							</TableRow>
						) : (
							sortedFilteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r) => (
								<TableRow
									key={r.student_id}
									hover
									sx={{ cursor: 'pointer' }}
									onClick={() => goToStudentPreview(r)}
								>
									<TableCell
										sx={{ py: 0.5, px: 0.5 }}
										onClick={(e) => e.stopPropagation()}
									>
										<Tooltip title='Edytuj'>
											<IconButton
												size='small'
												aria-label='Edytuj'
												disabled={loading}
												onClick={() => openStage3Edit(r)}
											>
												<EditOutlinedIcon fontSize='small' />
											</IconButton>
										</Tooltip>
									</TableCell>
									<TableCell>{r.certificate_number ?? '—'}</TableCell>
									<TableCell
										align='center'
										sx={{ py: 0.25, px: 0.25 }}
										onClick={(e) => e.stopPropagation()}
									>
										<Checkbox
											size='small'
											checked={!!r.exclude_from_bulk_export}
											disabled={excludeSavingId === r.student_id || loading}
											onChange={(_, checked) => void toggleExcludeFromBulkExport(r, checked)}
											inputProps={{ 'aria-label': 'Wyklucz z eksportu zbiorczego' }}
										/>
									</TableCell>
									<TableCell
										align='center'
										sx={{ py: 0.25, px: 0.25 }}
										onClick={(e) => e.stopPropagation()}
									>
										<Checkbox
											size='small'
											checked={!!r.r1_manual}
											disabled={r1ManualSavingId === r.student_id || loading}
											onChange={(_, checked) => void toggleR1Manual(r, checked)}
											inputProps={{ 'aria-label': 'Kohorta R1 — poprawki' }}
										/>
									</TableCell>
									<TableCell sx={{ maxWidth: 140, verticalAlign: 'top' }}>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
											<Typography
												component='span'
												variant='body2'
												sx={{ fontWeight: 600, lineHeight: 1.25 }}
											>
												{r.last_name}
											</Typography>
											<Typography
												component='span'
												variant='body2'
												color='text.secondary'
												sx={{ lineHeight: 1.25 }}
											>
												{r.first_name}
											</Typography>
										</Box>
									</TableCell>
									<TableCell>{r.district_name}</TableCell>
									<TableCell sx={{ maxWidth: 280, verticalAlign: 'top' }}>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
											<Typography
												variant='body2'
												sx={{ lineHeight: 1.25 }}
											>
												{r.final_title}
											</Typography>
											<Typography
												variant='body2'
												color='text.secondary'
												sx={{ lineHeight: 1.25 }}
											>
												{r.title_declaration}
											</Typography>
											{r.lokata_text != null && String(r.lokata_text).trim() !== '' && (
												<Typography
													variant='caption'
													color='text.secondary'
													sx={{ whiteSpace: 'normal', lineHeight: 1.35, display: 'block' }}
												>
													{r.lokata_text}
												</Typography>
											)}
										</Box>
									</TableCell>
									<TableCell sx={{ maxWidth: 140, verticalAlign: 'top' }}>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
											<Typography
												variant='body2'
												sx={{ lineHeight: 1.25 }}
											>
												{r.birthday_place || '—'}
											</Typography>
											<Typography
												variant='body2'
												color='text.secondary'
												sx={{ lineHeight: 1.25 }}
											>
												{r.city_locative || '—'}
											</Typography>
										</Box>
									</TableCell>
									<TableCell sx={{ maxWidth: 160, verticalAlign: 'top' }}>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
											<Typography
												variant='body2'
												sx={{ whiteSpace: 'normal', lineHeight: 1.25 }}
											>
												{r.birth_date_words}
											</Typography>
											<Typography
												variant='body2'
												color='text.secondary'
												sx={{ lineHeight: 1.25 }}
											>
												{r.class_roman || '—'}
											</Typography>
										</Box>
									</TableCell>
									<TableCell sx={{ maxWidth: 280, whiteSpace: 'normal' }}>{r.school_name_original}</TableCell>
									<TableCell sx={{ maxWidth: 280, whiteSpace: 'normal' }}>{r.school_genitive}</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component='div'
				rowsPerPageOptions={[10, 25, 50, 100]}
				count={sortedFilteredRows.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={(_, p) => setPage(p)}
				onRowsPerPageChange={(e) => {
					setRowsPerPage(parseInt(e.target.value, 10));
					setPage(0);
				}}
				labelRowsPerPage='Wierszy na stronę'
				labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
			/>

			<EnShippingDialog
				open={shippingDialogOpen}
				onClose={() => setShippingDialogOpen(false)}
				disabled={!token}
				step1Description={
					'Najpierw wygeneruj i zaimportuj przesyłki w EN. Przesyłka polecona krajowa — adres z profilu ucznia. ' +
					'numerPrzesylkiKlienta: OLiJP lub OLiJP-SP, „_edycja”, numer rzymski edycji olimpiady, id ucznia (np. ' +
					'OLiJP_edycjaVII_12345). Masa musi być zgodna z FORMAT_GABARYT_EPO: S — max 500 g, M — max 1000 g, L — max ' +
					'2000 g. Dopiero po imporcie pobierz z EN PDF z etykietami i użyj kroku 2 poniżej.'
				}
				step2Description={
					'PDF z Elektronicznego Nadawcy (jedna etykieta na stronę, np. ~101×54 mm) można ułożyć na arkuszu A4 w siatce ' +
					'2×5 (10 etykiet na stronę). Układ siatki i odstępy można dopasować w ustawieniach zaświadczeń III (poniżej ' +
					'„Zapisz ustawienia”). Skalowanie zachowuje proporcje (dopasowanie do komórki 105×57 mm).'
				}
				exportFile={(params) => exportStage3ShippingPostXlsx(token, cohort, params)}
				convertLabelsPdf={(fileBase64) => convertStage3EnLabelsPdfToSheet(token, fileBase64)}
			/>

			<Dialog
				open={editOpen && editRow !== null}
				onClose={() => !editSaving && closeStage3EditDialog()}
				fullWidth
				maxWidth='lg'
				PaperProps={{ sx: { width: 'min(1100px, 98vw)', maxHeight: '92vh' } }}
			>
				<DialogTitle>Edycja tekstów zaświadczenia</DialogTitle>
				<DialogContent
					dividers
					sx={{ pt: 2 }}
				>
					{editRow && (
						<>
							<Typography
								variant='body2'
								component='div'
								sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 2 }}
							>
								{editRow.last_name} {editRow.first_name} (ID {editRow.student_id})
							</Typography>
							<Grid
								container
								spacing={2}
							>
								<Grid
									item
									xs={12}
									md={6}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 1,
											minHeight: 24,
										}}
									>
										<Typography
											variant='overline'
											color='text.secondary'
											component='span'
											sx={{ lineHeight: 1.2 }}
										>
											Źródło: baza / automat (podgląd)
										</Typography>
										<Box
											sx={{
												width: 22,
												height: 22,
												flexShrink: 0,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											{editSuggestionsLoading && (
												<CircularProgress
													size={18}
													thickness={5}
													aria-label='Wczytywanie propozycji z morfologii'
												/>
											)}
										</Box>
									</Box>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
										<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Box sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
													<Typography
														variant='caption'
														color='text.secondary'
														sx={{ flex: 1, minWidth: 0 }}
													>
														Data urodzenia (jak w bazie)
													</Typography>
													<Typography
														variant='caption'
														color='text.secondary'
														sx={{ flex: 1, minWidth: 0 }}
													>
														Automat ze słownej daty
													</Typography>
												</Box>
												<Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
													<Typography
														variant='body2'
														component='pre'
														sx={{
															flex: 1,
															minWidth: 0,
															p: 0.75,
															borderRadius: 1,
															bgcolor: 'action.hover',
															fontFamily: 'ui-monospace, Consolas, monospace',
															fontSize: '0.8125rem',
															whiteSpace: 'pre-wrap',
															wordBreak: 'break-word',
															margin: 0,
														}}
													>
														{editRow.birthday_stored_raw?.trim()
															? editRow.birthday_stored_raw.trim()
															: editRow.birthday_db?.trim()
																? editRow.birthday_db
																: '—'}
													</Typography>
													<Typography
														variant='body2'
														sx={{
															flex: 1,
															minWidth: 0,
															p: 0.75,
															borderRadius: 1,
															bgcolor: 'action.hover',
															whiteSpace: 'pre-wrap',
															wordBreak: 'break-word',
														}}
													>
														{editRow.birth_date_words_suggested?.trim() ? editRow.birth_date_words_suggested : '—'}
													</Typography>
												</Box>
											</Box>
											<Tooltip title='Wstaw automat do pola „Data urodzenia (słownie)”'>
												<span>
													<IconButton
														size='small'
														aria-label='Wstaw automat słownej daty'
														disabled={!editRow.birth_date_words_suggested?.trim()}
														onClick={() =>
															setEditRow({
																...editRow,
																birth_date_words: editRow.birth_date_words_suggested?.trim() ?? '',
															})
														}
														sx={{ mb: 0.25 }}
													>
														<EastIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
										<Divider />
										<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Box sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
													<Typography
														variant='caption'
														color='text.secondary'
														sx={{ flex: 1, minWidth: 0 }}
													>
														Miejsce urodzenia (mianownik)
													</Typography>
													<Typography
														variant='caption'
														color='text.secondary'
														sx={{ flex: 1, minWidth: 0 }}
													>
														Propozycja miejscownika
													</Typography>
												</Box>
												<Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
													<Typography
														variant='body2'
														sx={{
															flex: 1,
															minWidth: 0,
															p: 0.75,
															borderRadius: 1,
															bgcolor: 'action.hover',
															whiteSpace: 'pre-wrap',
															wordBreak: 'break-word',
														}}
													>
														{editRow.birthday_place?.trim() ? editRow.birthday_place : '—'}
													</Typography>
													<Typography
														variant='body2'
														sx={{
															flex: 1,
															minWidth: 0,
															p: 0.75,
															borderRadius: 1,
															bgcolor: 'action.hover',
															whiteSpace: 'pre-wrap',
															wordBreak: 'break-word',
														}}
													>
														{editRow.city_locative_suggested?.trim() ? editRow.city_locative_suggested : '—'}
													</Typography>
												</Box>
											</Box>
											<Tooltip title='Wstaw propozycję do pola „Miasto — miejscownik”'>
												<span>
													<IconButton
														size='small'
														aria-label='Wstaw propozycję miejscownika'
														disabled={!editRow.city_locative_suggested?.trim()}
														onClick={() =>
															setEditRow({
																...editRow,
																city_locative: editRow.city_locative_suggested?.trim() ?? '',
															})
														}
														sx={{ mb: 0.25 }}
													>
														<EastIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
										<Divider />
										<Box>
											<Typography
												variant='caption'
												color='text.secondary'
												display='block'
											>
												Nazwa szkoły (baza)
											</Typography>
											<Typography
												variant='body2'
												sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre-wrap' }}
											>
												{editRow.school_name_original?.trim() ? editRow.school_name_original : '—'}
											</Typography>
										</Box>
										<Box>
											<Typography
												variant='caption'
												color='text.secondary'
												display='block'
											>
												Miasto szkoły (baza)
											</Typography>
											<Typography
												variant='body2'
												sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
											>
												{editRow.school_city?.trim() ? editRow.school_city : '—'}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography
													variant='caption'
													color='text.secondary'
													display='block'
													sx={{ mb: 0.25 }}
												>
													Propozycja dopełniacza (z nazwy szkoły)
												</Typography>
												<Typography
													variant='body2'
													sx={{
														p: 0.75,
														borderRadius: 1,
														bgcolor: 'action.hover',
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
													}}
												>
													{editRow.school_genitive_suggested?.trim() ? editRow.school_genitive_suggested : '—'}
												</Typography>
											</Box>
											<Tooltip title='Wstaw propozycję do pola „Szkoła — dopełniacz”'>
												<span>
													<IconButton
														size='small'
														aria-label='Wstaw propozycję dopełniacza szkoły'
														disabled={!editRow.school_genitive_suggested?.trim()}
														onClick={() =>
															setEditRow({
																...editRow,
																school_genitive: editRow.school_genitive_suggested?.trim() ?? '',
															})
														}
														sx={{ mb: 0.25 }}
													>
														<EastIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
										<Divider />
										<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Box sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
													<Typography
														variant='caption'
														color='text.secondary'
														sx={{ flex: 1, minWidth: 0 }}
													>
														Klasa w bazie
													</Typography>
													<Typography
														variant='caption'
														color='text.secondary'
														sx={{ flex: 1, minWidth: 0 }}
													>
														Propozycja klasy
													</Typography>
												</Box>
												<Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
													<Typography
														variant='body2'
														sx={{
															flex: 1,
															minWidth: 0,
															p: 0.75,
															borderRadius: 1,
															bgcolor: 'action.hover',
														}}
													>
														{editRow.class_display_db?.trim() ? editRow.class_display_db : '—'}
													</Typography>
													<Typography
														variant='body2'
														sx={{
															flex: 1,
															minWidth: 0,
															p: 0.75,
															borderRadius: 1,
															bgcolor: 'action.hover',
														}}
													>
														{editRow.class_roman_suggested?.trim() ? editRow.class_roman_suggested : '—'}
													</Typography>
												</Box>
											</Box>
											<Tooltip title='Wstaw propozycję do pola „Klasa (np. rzymska)”'>
												<span>
													<IconButton
														size='small'
														aria-label='Wstaw propozycję klasy'
														disabled={!editRow.class_roman_suggested?.trim()}
														onClick={() =>
															setEditRow({
																...editRow,
																class_roman: editRow.class_roman_suggested?.trim() ?? '',
															})
														}
														sx={{ mb: 0.25 }}
													>
														<EastIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</Box>
								</Grid>
								<Grid
									item
									xs={12}
									md={6}
								>
									<Typography
										variant='overline'
										color='text.secondary'
										display='block'
										sx={{ mb: 1 }}
									>
										W zaświadczeniu (edytowalne)
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
											<TextField
												label='Imię'
												value={editRow.first_name}
												onChange={(e) => setEditRow({ ...editRow, first_name: e.target.value })}
												size='small'
												sx={{ flex: '1 1 140px', minWidth: 120 }}
											/>
											<TextField
												label='Drugie imię'
												value={editRow.second_name ?? ''}
												onChange={(e) => setEditRow({ ...editRow, second_name: e.target.value })}
												size='small'
												sx={{ flex: '1 1 140px', minWidth: 120 }}
											/>
										</Box>
										<TextField
											label='Nazwisko'
											value={editRow.last_name}
											onChange={(e) => setEditRow({ ...editRow, last_name: e.target.value })}
											fullWidth
											size='small'
										/>
										<TextField
											label='Data urodzenia (słownie)'
											value={editRow.birth_date_words}
											onChange={(e) => setEditRow({ ...editRow, birth_date_words: e.target.value })}
											fullWidth
											size='small'
										/>
										<TextField
											label='Miasto — miejscownik (w …)'
											value={editRow.city_locative}
											onChange={(e) => setEditRow({ ...editRow, city_locative: e.target.value })}
											fullWidth
											size='small'
										/>
										<Button
											variant='outlined'
											size='small'
											sx={{ alignSelf: 'flex-start' }}
											disabled={!editRow.school_city?.trim()}
											onClick={() => void openCitySuffixDialog()}
										>
											Dopisz miasto do nazwy szkoły
										</Button>
										<TextField
											label='Szkoła — dopełniacz'
											multiline
											minRows={2}
											maxRows={8}
											value={editRow.school_genitive}
											onChange={(e) => setEditRow({ ...editRow, school_genitive: e.target.value })}
											helperText='Enter = nowa linia w PDF/HTML. „w”/„we”, „im.”, „nr”, „I”/„II”/„III”, cudzysłowy — NBSP jak w podglądzie.'
											fullWidth
										/>
										<TextField
											label='Klasa (np. rzymska)'
											value={editRow.class_roman}
											onChange={(e) => setEditRow({ ...editRow, class_roman: e.target.value })}
											fullWidth
											size='small'
										/>
									</Box>
								</Grid>
							</Grid>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => closeStage3EditDialog()}
						disabled={editSaving}
					>
						Anuluj
					</Button>
					<Button
						variant='contained'
						disabled={editSaving || !editRow}
						onClick={async () => {
							if (!token || !editRow) {
								return;
							}
							setEditSaving(true);
							try {
								const res = await updateStage3AttestRow(token, editRow.student_id, {
									first_name: editRow.first_name,
									last_name: editRow.last_name,
									second_name: editRow.second_name ?? '',
									birth_date_words: editRow.birth_date_words,
									city_locative: editRow.city_locative,
									school_genitive: editRow.school_genitive,
									class_roman: editRow.class_roman,
								});
								if (res.data.student) {
									setEditRow(res.data.student);
								}
								setMsg({ msg: 'Zapisano', severity: 'success' });
								closeStage3EditDialog();
								await loadStudents();
							} catch {
								setMsg({ msg: 'Błąd zapisu', severity: 'error' });
							} finally {
								setEditSaving(false);
							}
						}}
					>
						Zapisz
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={cityDialogOpen}
				onClose={() => !cityAppendBusy && setCityDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Dopisz lokalizację do nazwy szkoły</DialogTitle>
				<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					{cityPreviewLoading ? (
						<CircularProgress size={28} />
					) : (
						<>
							<Typography
								variant='body2'
								color='text.secondary'
							>
								Na końcu nazwy szkoły w bazie dopisywany jest fragment „{cityPrep} …” (miejscownik miasta). Dopełniacz w
								zaświadczeniu zostanie przeliczony automatycznie.
							</Typography>
							<TextField
								label='Miejscownik miasta (edytuj, jeśli automat jest niepoprawny)'
								value={cityLocDraft}
								onChange={(e) => setCityLocDraft(e.target.value)}
								fullWidth
							/>
							<Typography
								variant='caption'
								color='text.secondary'
							>
								Podgląd dopisku: {cityPrep} {cityLocDraft || '…'}
							</Typography>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setCityDialogOpen(false)}
						disabled={cityAppendBusy}
					>
						Anuluj
					</Button>
					<Button
						variant='contained'
						disabled={cityPreviewLoading || cityAppendBusy}
						onClick={() => void applyCitySuffix()}
					>
						{cityAppendBusy ? <CircularProgress size={22} /> : 'Zapisz do bazy i przelicz'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={downloadDialogOpen}
				onClose={() => !busy && setDownloadDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Pobierz</DialogTitle>
				<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
					<Typography
						variant='body2'
						color='text.secondary'
					>
						Bieżąca kohorta: {COHORTS.find((c) => c.id === cohort)?.label ?? cohort}
					</Typography>
					{exportExamDatesLinePreview.kind === 'single' && (
						<Box>
							<Typography
								variant='caption'
								color='text.secondary'
								display='block'
								sx={{ mb: 0.5 }}
							>
								{exportExamDatesLinePreview.settingLabel}
							</Typography>
							<Typography
								variant='body2'
								sx={{
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
									p: 1,
									borderRadius: 1,
									bgcolor: 'action.hover',
								}}
							>
								{exportExamDatesLinePreview.text || '— (puste)'}
							</Typography>
						</Box>
					)}
					{exportExamDatesLinePreview.kind === 'per_student' && settings && (
						<Box>
							<Typography
								variant='caption'
								color='text.secondary'
								display='block'
								sx={{ mb: 0.75 }}
							>
								W tej kohortcie treść środkowej linii jest dobierana osobno do każdego ucznia (krótsza lub pełna).
								Poniżej obie treści z ustawień:
							</Typography>
							<Typography
								variant='caption'
								fontWeight={600}
								display='block'
							>
								Pisemny + ustny
							</Typography>
							<Typography
								variant='body2'
								sx={{
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
									p: 1,
									mb: 1,
									borderRadius: 1,
									bgcolor: 'action.hover',
								}}
							>
								{(settings.exam_dates_line ?? '').trim() || '—'}
							</Typography>
							<Typography
								variant='caption'
								fontWeight={600}
								display='block'
							>
								Tylko pisemny
							</Typography>
							<Typography
								variant='body2'
								sx={{
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
									p: 1,
									borderRadius: 1,
									bgcolor: 'action.hover',
								}}
							>
								{(settings.exam_dates_line_written_only ?? '').trim() || '— (puste — wtedy używana pełna)'}
							</Typography>
						</Box>
					)}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onCsv()}
						>
							Pobierz CSV
						</Button>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onZip('preview')}
						>
							ZIP — podgląd (bez numeru)
						</Button>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onZip('numbered')}
						>
							ZIP — z numerem
						</Button>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onMergedPdf('preview')}
						>
							PDF zbiorczy — podgląd
						</Button>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onMergedPdf('numbered')}
						>
							PDF zbiorczy — z numerem
						</Button>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDownloadDialogOpen(false)}
						disabled={busy}
					>
						Zamknij
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={syncDialogOpen}
				onClose={() => !busy && setSyncDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Synchronizacja tekstów</DialogTitle>
				<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
					<Typography
						variant='body2'
						color='text.secondary'
					>
						Wybierz obszary do przeliczenia z bazy i wyników. Dopełniacz szkoły nie jest nadpisywany, jeśli w polu jest
						już znak nowej linii (ręczny podział).
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								checked={syncFillMissingOnly}
								onChange={(e) => setSyncFillMissingOnly(e.target.checked)}
							/>
						}
						label='Tylko uzupełnij puste pola w zaznaczonych obszarach (nie nadpisuj już wypełnionych tekstów)'
					/>
					<Typography
						variant='caption'
						color='text.secondary'
						display='block'
						sx={{ mt: -0.5, mb: 0.5 }}
					>
						Dotyczy: data słownie, miejscownik/dopełniacz miejsca, dopełniacz szkoły, klasa. Formy „urodzony/brał…” i
						tytuł z wyników są nadal przeliczane przy każdej synchronizacji częściowej.
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!syncAreas.birth_dates}
								onChange={(e) => setSyncAreas((a) => ({ ...a, birth_dates: e.target.checked }))}
							/>
						}
						label='Daty (data urodzenia słownie)'
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!syncAreas.birth_place}
								onChange={(e) => setSyncAreas((a) => ({ ...a, birth_place: e.target.checked }))}
							/>
						}
						label='Miejsce urodzenia (miejscownik)'
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!syncAreas.school}
								onChange={(e) => setSyncAreas((a) => ({ ...a, school: e.target.checked }))}
							/>
						}
						label='Szkoła (dopełniacz z nazwy w bazie)'
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!syncAreas.class}
								onChange={(e) => setSyncAreas((a) => ({ ...a, class: e.target.checked }))}
							/>
						}
						label='Klasa (rzymska / z pola)'
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setSyncDialogOpen(false)}
						disabled={busy}
					>
						Anuluj
					</Button>
					<Button
						variant='contained'
						disabled={busy}
						onClick={() => void onSync(syncAreas)}
					>
						Kontynuuj
					</Button>
				</DialogActions>
			</Dialog>

			<Stage3SignedScanDialog
				open={signedScanDialogOpen}
				onClose={() => setSignedScanDialogOpen(false)}
				cohort={cohort}
				cohortLabel={COHORTS.find((c) => c.id === cohort)?.label ?? cohort}
				token={token}
			/>
		</Box>
	);
};

export default Stage3AttestationsTab;
